import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/axios';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import SessionWarningDialog from '../components/SessionWarningDialog';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [userRole, setUserRole] = useState(null);
  const [fullname, setFullname] = useState(null);
  const [lastlogin, setLastlogin] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [hospitalId, setHospitalId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const navigate = useNavigate();

  const clearAuth = useCallback(() => {
    setIsAuthenticated(false);
    setUserRole(null);
    setFullname(null);
    setLastlogin(null);
    setPermissions([]);
    setHospitalId(null);
  }, []);

  const handleSessionLogout = useCallback(() => {
    clearAuth();
    navigate('/');
  }, [clearAuth, navigate]);

  const verifyAuth = async () => {
    try {
      const response = await api.get('/auth/verify');
      if (response.data.status === 'authenticated') {
        setIsAuthenticated(true);
        setUserRole(response.data.role);
        setFullname(response.data.fullname);
        setLastlogin(response.data.lastlogin);
        setPermissions(response.data.permissions || []);
        setHospitalId(response.data.hospital_id);
      } else {
        clearAuth();
      }
    } catch (error) {
      console.error("Auth verification failed:", error);
      clearAuth();
    } finally {
      setAuthChecked(true);
    }
  };

  useEffect(() => {
    verifyAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Global 401 Interceptors ───────────────────────────────────────────────
  useEffect(() => {
    const globalInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          const requestUrl = error.config?.url || '';
          if (!requestUrl.includes('/signin') && !requestUrl.includes('/auth/verify')) {
            console.warn("[AuthContext] Global Axios: Session expired (401). Redirecting...");
            clearAuth();
            navigate('/');
          }
        }
        return Promise.reject(error);
      }
    );

    const apiInterceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          const requestUrl = error.config?.url || '';
          if (!requestUrl.includes('/signin') && !requestUrl.includes('/auth/verify')) {
            console.warn("[AuthContext] Custom API: Session expired (401). Redirecting...");
            clearAuth();
            navigate('/');
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(globalInterceptor);
      api.interceptors.response.eject(apiInterceptor);
    };
  }, [clearAuth, navigate]);

  // ── Window Focus / Tab Visibility Listeners ───────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleFocusOrVisibility = () => {
      console.log("[AuthContext] Window focused or tab visible. Verifying session...");
      verifyAuth();
    };

    window.addEventListener('focus', handleFocusOrVisibility);
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleFocusOrVisibility();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocusOrVisibility);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  // ── Session timeout warning ────────────────────────────────────────────────
  const { showWarning, countdown, stayLoggedIn, doLogout } = useSessionTimeout({
    isAuthenticated,
    onLogout: handleSessionLogout,
  });

  return (
    <AuthContext.Provider value={{
      userRole,
      fullname,
      lastlogin,
      permissions,
      hospitalId,
      isAuthenticated,
      authChecked,
      verifyAuth,
      setUserRole,
      setFullname,
      setLastlogin,
      setPermissions,
      setHospitalId,
      setIsAuthenticated
    }}>
      {children}

      {/* Global session expiry warning — shown on top of everything */}
      <SessionWarningDialog
        open={showWarning}
        countdown={countdown}
        onStayLoggedIn={stayLoggedIn}
        onLogout={doLogout}
      />
    </AuthContext.Provider>
  );
};
